(function twoNuActivitySelectorModule() {
  "use strict";

  if (customElements.get("two-nu-activity-selector")) return;

  const MEMORY_STORAGE = new Map();
  const DEFAULT_STORAGE_KEY = "twoNu.activitySelector.popup.v1";
  const DEFAULT_SUPPRESSION_MS = 86_400_000;
  const COLLISION_GAP = 12;
  const AUTO_OPEN_POLL_MS = 250;
  const AUTO_OPEN_WAIT_MS = 10_000;
  /* 2026-09-18: B1G1F flash promo owns homepage auto-show. Keep Sport Match code; do not auto-open. */
  const DISABLE_HOMEPAGE_AUTO_OPEN = true;
  const FOCUSABLE_SELECTOR = [
    "a[href]",
    "button:not([disabled])",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
  ].join(",");

  const parseJson = (node, fallback) => {
    try {
      return JSON.parse(node?.textContent || "");
    } catch (error) {
      console.error("2nu activity selector JSON could not be parsed.", error);
      return fallback;
    }
  };

  const isVisible = (element) => {
    if (!(element instanceof Element) || element.hidden) return false;
    const style = getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== "none"
      && style.visibility !== "hidden"
      && Number(style.opacity || 1) > 0
      && rect.width > 0
      && rect.height > 0;
  };

  const fixedOrStickyAncestor = (element) => {
    let current = element;
    while (current && current !== document.documentElement) {
      const position = getComputedStyle(current).position;
      if (position === "fixed" || position === "sticky") return current;
      current = current.parentElement;
    }
    return null;
  };

  const intersectsWithGap = (left, right, gap) => (
    left.left < right.right + gap
    && left.right > right.left - gap
    && left.top < right.bottom + gap
    && left.bottom > right.top - gap
  );

  class TwoNuActivitySelector extends HTMLElement {
    connectedCallback() {
      if (this.portalMoving || this.dataset.twoNuActivitySelectorReady === "true") return;

      this.core = window.TwoNuActivityCore;
      if (!this.core) {
        console.error("2nu activity selector core is unavailable.");
        return;
      }

      this.config = parseJson(
        this.querySelector("[data-two-nu-activity-selector-config]"),
        { i18n: {}, analytics: {} }
      );
      this.mode = String(this.config.mode || (this.config.isHomepage === true ? "homepage" : "manual"));
      this.isHomepage = this.mode === "homepage";
      this.isEmbedded = this.mode === "embedded";
      this.isGlobal = this.config.isGlobal === true
        || this.hasAttribute("data-two-nu-activity-global-controller");
      this.portalGlobalController();

      this.registry = parseJson(
        this.querySelector("[data-two-nu-activity-registry]"),
        { destinations: {}, fallback: {} }
      );
      this.dialog = this.querySelector("[data-two-nu-activity-dialog]");
      this.heading = this.querySelector("[data-two-nu-activity-heading]");
      this.minimizeButton = this.querySelector("[data-two-nu-activity-minimize]");
      this.launcher = this.querySelector("[data-two-nu-activity-launcher]");
      this.inputs = Array.from(this.querySelectorAll(".two-nu-activity-selector__input"));
      this.group = this.querySelector("[data-two-nu-activity-options]");
      this.status = this.querySelector("[data-two-nu-activity-status]");
      this.cta = this.querySelector("[data-two-nu-activity-cta]");
      this.ctaLabel = this.querySelector("[data-two-nu-activity-cta-label]");
      this.methodTrigger = this.querySelector("[data-two-nu-activity-method-open]");
      this.methodLayer = this.querySelector("[data-two-nu-activity-method]");
      this.methodClose = this.querySelector("[data-two-nu-activity-method-close]");
      this.methodTitle = this.querySelector("[data-two-nu-activity-method-title]");
      this.methodInertTargets = [
        this.querySelector(".two-nu-activity-selector__scroll"),
        this.querySelector(".two-nu-activity-selector__action-row"),
        this.minimizeButton,
      ].filter(Boolean);
      this.methodBackgroundState = new Map();
      this.maxSelections = Math.min(
        this.core.MAX_SELECTIONS || 3,
        Math.max(1, Number(this.config.maxSelections) || 3)
      );
      this.storageKey = String(this.config.storageKey || DEFAULT_STORAGE_KEY);
      this.suppressionMs = Math.max(
        1,
        Number(this.config.suppressionMs) || DEFAULT_SUPPRESSION_MS
      );
      this.themeEditor = this.config.designMode === true || Boolean(window.Shopify?.designMode);
      this.openMode = "";
      this.openAnimation = null;
      this.pendingOpenTargetRect = null;
      this.autoOpenDeadline = 0;
      this.dataset.popupState = "booting";

      if (
        !this.dialog
        || !this.group
        || !this.cta
        || (!this.isEmbedded && !this.launcher)
      ) {
        console.error("2nu activity selector popup markup is incomplete.");
        return;
      }

      this.handleInputChange = (event) => this.onChange(event);
      this.handleGroupKeydown = (event) => this.onKeydown(event);
      this.handleCtaClick = (event) => this.onCtaClick(event);
      this.handleMethodOpenClick = () => this.openMethod();
      this.handleMethodCloseClick = () => this.closeMethod({ focus: true });
      this.handleMethodKeydown = (event) => {
        if (!this.methodIsOpen()) return;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          this.closeMethod({ focus: true });
          return;
        }
        if (event.key === "Tab") this.trapMethodFocus(event);
      };
      this.handleMinimizeClick = () => this.minimize("minimize");
      this.handleLauncherClick = (event) => {
        this.manualKeyboardOpen = Number(event?.detail) === 0;
        this.emitAnalytics("launcherOpen");
        this.open("launcher");
      };
      this.handleDialogCancel = (event) => {
        event.preventDefault();
        if (this.methodIsOpen()) {
          this.closeMethod({ focus: true });
          return;
        }
        this.minimize("escape");
      };
      this.handleDialogClose = () => {
        this.closeMethod({ focus: false });
        if (this.dataset.popupState === "navigating") return;
        if (this.skipNextDialogClose) {
          this.skipNextDialogClose = false;
          return;
        }
        this.cancelMinimizeAnimations();
        this.showLauncher({ focus: false });
      };
      this.handleGlobalDialogKeydown = (event) => {
        if (!this.isGlobal || !this.dialog?.open) return;
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          if (this.methodIsOpen()) {
            this.closeMethod({ focus: true });
            return;
          }
          this.minimize("escape");
          return;
        }
        if (event.key === "Tab") {
          if (this.methodIsOpen()) {
            event.stopPropagation();
            this.trapMethodFocus(event);
            return;
          }
          this.trapDialogFocus(event);
        }
      };
      this.handleGlobalDialogClickCapture = (event) => {
        if (!this.isGlobal || !this.dialog?.open || !(event.target instanceof Element)) return;
        if (
          this.dialog.contains(event.target)
          || event.target.closest("[data-two-nu-global-dock]")
        ) return;
        event.preventDefault();
        event.stopPropagation();
        this.minimize("backdrop");
      };
      this.handleViewportChange = () => this.scheduleLauncherPosition();
      this.handlePageShow = (event) => {
        if (event.persisted) this.syncFromBrowserState();
      };
      this.handleOpenTriggerClick = (event) => {
        const trigger = event.target.closest?.("[data-two-nu-open-activity-selector]");
        if (!trigger || !this.ownsExternalOpenRequests()) return;
        event.preventDefault();
        this.openInvoker = trigger;
        this.manualKeyboardOpen = Number(event?.detail) === 0;
        if (
          trigger.matches("[data-two-nu-activity-dock-trigger]")
          && this.dialog.open
        ) {
          this.minimize("dock-toggle");
          return;
        }
        this.emitAnalytics("launcherOpen", {
          source: trigger.matches("[data-two-nu-activity-dock-trigger]")
            ? "dock-token"
            : "page-trigger",
        });
        this.open("launcher");
      };
      this.handleOpenRequest = () => this.open("launcher");

      this.inputs.forEach((input) => input.addEventListener("change", this.handleInputChange));
      this.group.addEventListener("keydown", this.handleGroupKeydown);
      this.cta.addEventListener("click", this.handleCtaClick);
      this.methodTrigger?.addEventListener("click", this.handleMethodOpenClick);
      this.methodClose?.addEventListener("click", this.handleMethodCloseClick);
      this.addEventListener("keydown", this.handleMethodKeydown);
      this.minimizeButton?.addEventListener("click", this.handleMinimizeClick);
      this.launcher?.addEventListener("click", this.handleLauncherClick);
      if (!this.isEmbedded) {
        this.dialog.addEventListener("cancel", this.handleDialogCancel);
        this.dialog.addEventListener("close", this.handleDialogClose);
      }
      if (this.isGlobal) {
        this.handlePortalSectionUnload = (event) => {
          if (event.target === this.portalSourceSection) this.remove();
        };
        document.addEventListener("shopify:section:unload", this.handlePortalSectionUnload);
        document.addEventListener("keydown", this.handleGlobalDialogKeydown, true);
        document.addEventListener("click", this.handleGlobalDialogClickCapture, true);
        this.globalCloseApi = (reason, options) => {
          if (!this.dialog?.open) return false;
          this.minimize(
            reason || "dock-switch",
            Object.assign({ persist: false, focus: false }, options || {})
          );
          return true;
        };
        window.twoNuCloseActivitySelector = this.globalCloseApi;
      }
      this.externalOpenOwner = this.ownsExternalOpenRequests();
      if (this.externalOpenOwner) {
        document.addEventListener("click", this.handleOpenTriggerClick);
        document.addEventListener("two-nu:activity-selector-open", this.handleOpenRequest);
      }
      window.addEventListener("resize", this.handleViewportChange, { passive: true });
      window.addEventListener("orientationchange", this.handleViewportChange, { passive: true });
      window.addEventListener("pageshow", this.handlePageShow);
      window.visualViewport?.addEventListener("resize", this.handleViewportChange, { passive: true });
      window.visualViewport?.addEventListener("scroll", this.handleViewportChange, { passive: true });

      if (!this.isEmbedded && "MutationObserver" in window) {
        this.obstacleObserver = new MutationObserver(() => {
          this.scheduleLauncherPosition();
          this.syncDockTriggerState(Boolean(this.dialog?.open));
        });
        this.obstacleObserver.observe(document.body, {
          attributes: true,
          childList: true,
          subtree: true,
          attributeFilter: ["class", "hidden", "open"],
        });
      }

      this.dataset.twoNuActivitySelectorReady = "true";
      this.update();
      if (this.isEmbedded) {
        this.dataset.popupState = "embedded";
      } else {
        this.syncFromBrowserState();
      }
    }

    disconnectedCallback() {
      // Moving the controller keeps its children, selection and listeners intact.
      if (this.portalMoving) return;
      this.closeMethod({ focus: false });
      this.inputs?.forEach((input) => input.removeEventListener("change", this.handleInputChange));
      this.group?.removeEventListener("keydown", this.handleGroupKeydown);
      this.cta?.removeEventListener("click", this.handleCtaClick);
      this.methodTrigger?.removeEventListener("click", this.handleMethodOpenClick);
      this.methodClose?.removeEventListener("click", this.handleMethodCloseClick);
      this.removeEventListener("keydown", this.handleMethodKeydown);
      this.minimizeButton?.removeEventListener("click", this.handleMinimizeClick);
      this.launcher?.removeEventListener("click", this.handleLauncherClick);
      this.dialog?.removeEventListener("cancel", this.handleDialogCancel);
      this.dialog?.removeEventListener("close", this.handleDialogClose);
      document.removeEventListener("keydown", this.handleGlobalDialogKeydown, true);
      document.removeEventListener("click", this.handleGlobalDialogClickCapture, true);
      document.removeEventListener("shopify:section:unload", this.handlePortalSectionUnload);
      if (window.twoNuCloseActivitySelector === this.globalCloseApi) {
        delete window.twoNuCloseActivitySelector;
      }
      if (this.externalOpenOwner) {
        document.removeEventListener("click", this.handleOpenTriggerClick);
        document.removeEventListener("two-nu:activity-selector-open", this.handleOpenRequest);
      }
      window.removeEventListener("resize", this.handleViewportChange);
      window.removeEventListener("orientationchange", this.handleViewportChange);
      window.removeEventListener("pageshow", this.handlePageShow);
      window.visualViewport?.removeEventListener("resize", this.handleViewportChange);
      window.visualViewport?.removeEventListener("scroll", this.handleViewportChange);
      this.obstacleObserver?.disconnect();
      if (this.autoOpenTimer) window.clearTimeout(this.autoOpenTimer);
      this.cancelMinimizeAnimations();
      if (this.launcherFrame) window.cancelAnimationFrame(this.launcherFrame);
      delete this.dataset.twoNuActivitySelectorReady;
    }

    portalGlobalController() {
      // Only the global popup escapes section/ancestor stacking contexts.
      // Embedded Online Fit selectors retain their normal-flow semantics.
      if (!this.isGlobal || this.isEmbedded) return;
      this.dataset.twoNuOverlayPortal = "activity";
      if (this.parentElement === document.body) return;
      this.portalSourceSection = this.closest(".shopify-section");
      this.portalMoving = true;
      try {
        document.body.appendChild(this);
      } finally {
        this.portalMoving = false;
      }
    }

    ownsExternalOpenRequests() {
      if (this.isGlobal) return true;
      return !this.isEmbedded
        && !document.querySelector("[data-two-nu-activity-global-controller]");
    }

    dockTriggers() {
      return Array.from(
        document.querySelectorAll("[data-two-nu-activity-dock-trigger]")
      );
    }

    visibleDockTrigger() {
      return this.dockTriggers().find((trigger) => isVisible(trigger)) || null;
    }

    visibleDockFace() {
      const trigger = this.visibleDockTrigger();
      if (!trigger) return null;
      const face = trigger.querySelector("[data-two-nu-activity-dock-face]");
      return isVisible(face) ? face : trigger;
    }

    syncDockTriggerState(expanded) {
      if (!this.dialog?.id) return;
      this.dockTriggers().forEach((trigger) => {
        trigger.setAttribute("aria-controls", this.dialog.id);
        trigger.setAttribute("aria-haspopup", "dialog");
        trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
      });
    }

    focusableWithin(container) {
      if (!container) return [];
      return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => (
        isVisible(element)
        && !element.closest("[inert], [aria-hidden='true']")
      ));
    }

    trapFocusWithin(event, container, fallback) {
      const focusable = this.focusableWithin(container);
      if (!focusable.length) {
        event.preventDefault();
        fallback?.focus({ preventScroll: true });
        return;
      }
      const activeIndex = focusable.indexOf(document.activeElement);
      const moveToEnd = event.shiftKey && activeIndex <= 0;
      const moveToStart = !event.shiftKey && (
        activeIndex === -1
        || activeIndex === focusable.length - 1
      );
      if (!moveToEnd && !moveToStart) return;
      event.preventDefault();
      focusable[moveToEnd ? focusable.length - 1 : 0].focus({ preventScroll: true });
    }

    trapDialogFocus(event) {
      this.trapFocusWithin(event, this.dialog, this.heading);
    }

    trapMethodFocus(event) {
      this.trapFocusWithin(event, this.methodLayer, this.methodTitle || this.methodClose);
    }

    selectedIds() {
      return this.core.canonicalizeActivityIds(
        this.inputs.filter((input) => input.checked).map((input) => input.value),
        { min: 0, max: this.maxSelections }
      );
    }

    announce(message) {
      if (this.status) this.status.textContent = String(message || "");
    }

    methodIsOpen() {
      return Boolean(this.methodLayer && !this.methodLayer.hidden);
    }

    setMethodBackgroundInert(shouldBeInert) {
      this.methodInertTargets.forEach((target) => {
        if (shouldBeInert) {
          if (!this.methodBackgroundState.has(target)) {
            this.methodBackgroundState.set(target, {
              inert: target.inert === true,
              ariaHidden: target.getAttribute("aria-hidden"),
            });
          }
          target.inert = true;
          target.setAttribute("aria-hidden", "true");
          return;
        }

        const previous = this.methodBackgroundState.get(target);
        if (!previous) return;
        target.inert = previous.inert === true;
        if (previous.ariaHidden == null) target.removeAttribute("aria-hidden");
        else target.setAttribute("aria-hidden", previous.ariaHidden);
      });
      if (!shouldBeInert) this.methodBackgroundState.clear();
    }

    openMethod() {
      if (!this.methodLayer || !this.methodTrigger || this.methodIsOpen()) return;
      this.methodReturnFocus = document.activeElement instanceof HTMLElement
        && this.contains(document.activeElement)
        ? document.activeElement
        : this.methodTrigger;
      this.methodLayer.hidden = false;
      this.methodLayer.scrollTop = 0;
      this.methodTrigger.setAttribute("aria-expanded", "true");
      this.dataset.methodState = "open";
      const initialFocus = this.methodTitle || this.methodClose || this.methodLayer;
      initialFocus?.focus({ preventScroll: true });
      this.setMethodBackgroundInert(true);
      window.requestAnimationFrame(() => {
        if (!this.methodIsOpen() || this.methodLayer.contains(document.activeElement)) return;
        initialFocus?.focus({ preventScroll: true });
      });
    }

    closeMethod(options) {
      const settings = Object.assign({ focus: false }, options || {});
      if (!this.methodLayer) return;
      const wasOpen = this.methodIsOpen();
      const returnFocus = this.methodReturnFocus?.isConnected
        ? this.methodReturnFocus
        : this.methodTrigger;
      this.setMethodBackgroundInert(false);
      this.methodLayer.hidden = true;
      this.methodTrigger?.setAttribute("aria-expanded", "false");
      delete this.dataset.methodState;
      this.methodReturnFocus = null;
      if (wasOpen && settings.focus) {
        window.requestAnimationFrame(() => returnFocus?.focus({ preventScroll: true }));
      }
    }

    onChange(event) {
      const selectedCount = this.inputs.filter((input) => input.checked).length;
      if (event.currentTarget.checked && selectedCount > this.maxSelections) {
        event.currentTarget.checked = false;
        this.update({ keepStatus: true });
        this.announce(this.config.i18n?.statusMax);
        event.currentTarget.focus();
        return;
      }

      this.update();
      this.emitAnalytics("activityToggle", {
        activityId: event.currentTarget.value,
        selected: event.currentTarget.checked,
      });
    }

    onKeydown(event) {
      const input = event.target.closest?.(".two-nu-activity-selector__input");
      if (!input) return;

      const currentIndex = this.inputs.indexOf(input);
      let targetIndex = null;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        targetIndex = (currentIndex + 1) % this.inputs.length;
      }
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        targetIndex = (currentIndex - 1 + this.inputs.length) % this.inputs.length;
      }
      if (event.key === "Home") targetIndex = 0;
      if (event.key === "End") targetIndex = this.inputs.length - 1;
      if (targetIndex === null) return;

      event.preventDefault();
      this.inputs[targetIndex]?.focus();
    }

    onCtaClick(event) {
      if (this.cta.getAttribute("aria-disabled") === "true") {
        event.preventDefault();
        return;
      }

      if (this.isHomepage) this.writeSuppression("submit");
      this.dataset.popupState = "navigating";
      this.cta.setAttribute("aria-disabled", "true");
      this.emitAnalytics("matchSubmit", {
        routeKey: this.cta.dataset.routeKey || "",
        usedFallback: this.cta.dataset.registryFallback === "true",
      });
    }

    update(options) {
      const settings = Object.assign({ keepStatus: false }, options || {});
      const selected = this.selectedIds();
      const atMaximum = selected.length >= this.maxSelections;

      this.inputs.forEach((input) => {
        const unavailable = atMaximum && !input.checked;
        input.setAttribute("aria-disabled", unavailable ? "true" : "false");
        input.closest(".two-nu-activity-selector__option")
          ?.classList.toggle("is-unavailable", unavailable);
      });

      if (selected.length === 0) {
        this.setCtaDisabled();
        if (!settings.keepStatus) this.announce(this.config.i18n?.statusNone);
        return;
      }

      const resolved = this.core.resolveDestinationUrl(this.registry, selected);
      this.cta.href = resolved.url;
      this.cta.removeAttribute("tabindex");
      this.cta.setAttribute("aria-disabled", "false");
      this.cta.dataset.routeKey = resolved.routeKey;
      this.cta.dataset.registryFallback = resolved.usedFallback ? "true" : "false";

      const labels = {
        1: this.config.i18n?.ctaSingle,
        2: this.config.i18n?.ctaPair,
        3: this.config.i18n?.ctaTriple,
      };
      const statuses = {
        1: this.config.i18n?.statusOne,
        2: this.config.i18n?.statusTwo,
        3: this.config.i18n?.statusThree,
      };
      this.ctaLabel.textContent = labels[selected.length] || this.config.i18n?.ctaSingle || "";
      if (!settings.keepStatus) this.announce(statuses[selected.length] || "");
    }

    setCtaDisabled() {
      if (!this.cta || !this.ctaLabel) return;
      this.cta.href = "#";
      this.cta.setAttribute("aria-disabled", "true");
      this.cta.setAttribute("tabindex", "-1");
      delete this.cta.dataset.routeKey;
      delete this.cta.dataset.registryFallback;
      this.ctaLabel.textContent = this.config.i18n?.ctaDisabled || "";
    }

    readStoredRecord(storage) {
      try {
        const raw = storage?.getItem(this.storageKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!Number.isFinite(Number(parsed?.expiresAt))) {
          storage.removeItem(this.storageKey);
          return null;
        }
        return parsed;
      } catch {
        return null;
      }
    }

    browserStorages() {
      const storages = [];
      try {
        if (window.localStorage) storages.push(window.localStorage);
      } catch {
        // Continue to session or memory storage.
      }
      try {
        if (window.sessionStorage) storages.push(window.sessionStorage);
      } catch {
        // Continue to memory storage.
      }
      return storages;
    }

    suppressionRecord() {
      if (this.themeEditor) return null;
      const now = Date.now();
      const validRecords = [];
      for (const storage of this.browserStorages()) {
        const record = this.readStoredRecord(storage);
        if (!record) continue;
        if (Number(record.expiresAt) <= now) {
          try {
            storage.removeItem(this.storageKey);
          } catch {
            // Continue: another storage tier may still hold a valid record.
          }
          continue;
        }
        validRecords.push(record);
      }
      const memoryRecord = MEMORY_STORAGE.get(this.storageKey) || null;
      if (memoryRecord && Number(memoryRecord.expiresAt) > now) {
        validRecords.push(memoryRecord);
      } else if (memoryRecord) {
        MEMORY_STORAGE.delete(this.storageKey);
      }
      if (!validRecords.length) return null;
      return validRecords.sort(
        (left, right) => Number(right.expiresAt) - Number(left.expiresAt)
      )[0];
    }

    writeSuppression(reason) {
      if (!this.isHomepage || this.themeEditor) return;
      const record = {
        expiresAt: Date.now() + this.suppressionMs,
        reason: String(reason || "minimize"),
      };
      const serialized = JSON.stringify(record);
      for (const storage of this.browserStorages()) {
        try {
          storage.setItem(this.storageKey, serialized);
          return;
        } catch {
          // Continue to the next browser-local fallback.
        }
      }
      MEMORY_STORAGE.set(this.storageKey, record);
    }

    clearSuppression() {
      for (const storage of this.browserStorages()) {
        try {
          storage.removeItem(this.storageKey);
        } catch {
          // A disabled storage API is an expected fallback condition.
        }
      }
      MEMORY_STORAGE.delete(this.storageKey);
    }

    hasIntentionalHash() {
      return Boolean(window.location.hash && window.location.hash !== "#activity-selector");
    }

    activeAiSavingCampaign() {
      const campaign = document.querySelector(
        ".two-nu-hero--ai-saving[data-two-nu-home-campaign][data-campaign-start][data-campaign-end]"
      );
      if (!campaign) return false;
      const start = Date.parse(campaign.dataset.campaignStart || "");
      const end = Date.parse(campaign.dataset.campaignEnd || "");
      if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return false;
      const now = Date.now();
      return now >= start && now < end;
    }

    competingLayerOpen() {
      const openDialog = Array.from(document.querySelectorAll("dialog[open]"))
        .some((dialog) => dialog !== this.dialog && isVisible(dialog));
      if (openDialog) return true;

      return Array.from(document.querySelectorAll(
        '[role="dialog"][aria-modal="true"], cart-drawer.active, menu-drawer[open], .modal.active'
      )).some((element) => !this.contains(element) && isVisible(element));
    }

    syncFromBrowserState() {
      if (this.autoOpenTimer) {
        window.clearTimeout(this.autoOpenTimer);
        this.autoOpenTimer = null;
      }
      if (!this.isHomepage) {
        if (this.dialog.open) this.dialog.close();
        this.showLauncher({ focus: false });
        return;
      }
      if (DISABLE_HOMEPAGE_AUTO_OPEN) {
        if (this.dialog.open && this.openMode === "auto") this.dialog.close();
        if (!this.dialog.open) this.showLauncher({ focus: false });
        return;
      }
      if (this.activeAiSavingCampaign()) {
        if (this.dialog.open && this.openMode === "auto") this.dialog.close();
        if (!this.dialog.open) this.showLauncher({ focus: false });
        return;
      }
      if (this.suppressionRecord() || (!this.themeEditor && this.hasIntentionalHash())) {
        if (this.dialog.open) this.dialog.close();
        this.showLauncher({ focus: false });
        return;
      }
      this.autoOpenDeadline = Date.now() + AUTO_OPEN_WAIT_MS;
      this.scheduleAutoOpen();
    }

    scheduleAutoOpen() {
      if (this.dialog.open || this.dataset.popupState === "navigating") return;
      if (this.activeAiSavingCampaign()) {
        this.showLauncher({ focus: false });
        return;
      }
      if (!this.competingLayerOpen()) {
        this.open("auto");
        return;
      }
      this.showLauncher({ focus: false });
      if (Date.now() >= this.autoOpenDeadline) return;
      this.autoOpenTimer = window.setTimeout(() => this.scheduleAutoOpen(), AUTO_OPEN_POLL_MS);
    }

    open(mode) {
      if (this.isEmbedded) {
        this.closeMethod({ focus: false });
        this.scrollIntoView({ behavior: "smooth", block: "start" });
        this.heading?.focus({ preventScroll: true });
        return;
      }
      if (this.dialog.open) return;
      this.closeMethod({ focus: false });
      if (
        mode === "launcher"
        && this.isGlobal
        && window.TwoNuCompare
        && typeof window.TwoNuCompare.dialogIsOpen === "function"
        && window.TwoNuCompare.dialogIsOpen()
      ) {
        window.TwoNuCompare.close("dock-switch", { suppressFocusRestore: true });
      }
      if (this.competingLayerOpen()) {
        if (mode === "auto") {
          this.autoOpenTimer = window.setTimeout(
            () => this.scheduleAutoOpen(),
            AUTO_OPEN_POLL_MS
          );
        }
        return;
      }

      this.cancelMinimizeAnimations();
      this.openMode = mode === "launcher" ? "launcher" : "auto";
      this.dataset.popupState = this.openMode === "auto" ? "expanded-auto" : "expanded-manual";
      this.dialog.classList.remove("is-minimizing");
      this.dialog.classList.add("is-opening");
      const openingTarget = this.visibleDockFace()
        || (!this.launcher.hidden ? this.launcher : null);
      this.pendingOpenTargetRect = openingTarget?.getBoundingClientRect() || null;
      this.heading?.toggleAttribute(
        "data-two-nu-auto-focus",
        this.openMode === "auto"
      );
      this.heading?.toggleAttribute(
        "data-two-nu-manual-keyboard-focus",
        this.openMode === "launcher" && this.manualKeyboardOpen === true
      );
      this.launcher.classList.remove("is-animation-target");
      this.launcher.removeAttribute("aria-hidden");
      this.launcher.removeAttribute("tabindex");
      this.launcher.hidden = true;
      this.launcher.setAttribute("aria-expanded", "true");
      this.syncDockTriggerState(true);

      try {
        if (this.isGlobal && typeof this.dialog.show === "function") {
          this.dialog.show();
          this.dialog.removeAttribute("aria-modal");
        } else {
          this.dialog.showModal();
        }
      } catch (error) {
        console.error("2nu activity selector dialog could not open.", error);
        this.showLauncher({ focus: false });
        return;
      }

      this.heading?.focus({ preventScroll: true });
      this.heading?.addEventListener(
        "blur",
        () => {
          this.heading?.removeAttribute("data-two-nu-auto-focus");
          this.heading?.removeAttribute("data-two-nu-manual-keyboard-focus");
        },
        { once: true }
      );
      this.manualKeyboardOpen = false;
      this.emitAnalytics("popupImpression");

      window.requestAnimationFrame(() => {
        this.dialog.classList.remove("is-opening");
        this.animateOpenFromLauncher();
      });
    }

    animateOpenFromLauncher() {
      if (!this.dialog?.open) return;
      if (this.openAnimation) {
        this.openAnimation.cancel();
        this.openAnimation = null;
      }
      const targetRect = this.pendingOpenTargetRect;
      this.pendingOpenTargetRect = null;
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      if (
        reducedMotion
        || !targetRect
        || targetRect.width <= 0
        || targetRect.height <= 0
        || typeof this.dialog.animate !== "function"
      ) return;

      const dialogRect = this.dialog.getBoundingClientRect();
      if (dialogRect.width <= 0 || dialogRect.height <= 0) return;
      const translateX = targetRect.left + targetRect.width / 2
        - (dialogRect.left + dialogRect.width / 2);
      const translateY = targetRect.top + targetRect.height / 2
        - (dialogRect.top + dialogRect.height / 2);
      const targetScale = Math.max(
        0.055,
        Math.min(
          0.16,
          targetRect.width / dialogRect.width,
          targetRect.height / dialogRect.height
        )
      );
      const animation = this.dialog.animate([
        {
          opacity: 0.08,
          transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${targetScale})`,
          transformOrigin: "center center",
        },
        {
          offset: 0.54,
          opacity: 0.9,
          transform: `translate3d(${translateX * 0.24}px, ${translateY * 0.24}px, 0) scale(${0.78 + targetScale * 0.22})`,
          transformOrigin: "center center",
        },
        {
          opacity: 1,
          transform: "translate3d(0, 0, 0) scale(1)",
          transformOrigin: "center center",
        },
      ], {
        duration: 340,
        easing: "cubic-bezier(0.22, 0.78, 0.2, 1)",
        fill: "both",
      });
      this.openAnimation = animation;
      const finish = () => {
        if (this.openAnimation !== animation) return;
        animation.onfinish = null;
        animation.oncancel = null;
        animation.cancel();
        this.openAnimation = null;
      };
      animation.onfinish = finish;
      animation.oncancel = () => {
        if (this.openAnimation === animation) this.openAnimation = null;
      };
    }

    minimize(reason, options) {
      const settings = Object.assign({ persist: true, focus: true, immediate: false }, options || {});
      this.closeMethod({ focus: false });
      if (settings.persist && this.isHomepage) this.writeSuppression(reason);
      this.emitAnalytics("popupMinimize", { reason: String(reason || "minimize") });
      if (!this.dialog.open) {
        this.showLauncher({ focus: settings.focus });
        return;
      }

      this.cancelMinimizeAnimations();
      this.dataset.popupState = "minimizing";
      this.dialog.classList.add("is-minimizing");
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
      const dockTrigger = this.visibleDockTrigger();
      const dockFace = this.visibleDockFace();
      const animationTarget = dockFace || this.launcher;
      this.minimizeTargetTrigger = dockTrigger;
      let finalized = false;
      const runId = (this.minimizeRunId || 0) + 1;
      this.minimizeRunId = runId;
      const finalize = () => {
        if (finalized || this.minimizeRunId !== runId) return;
        finalized = true;
        if (this.minimizeFallbackTimer) {
          window.clearTimeout(this.minimizeFallbackTimer);
          this.minimizeFallbackTimer = null;
        }
        const minimizeAnimation = this.minimizeAnimation;
        const launcherAnimation = this.launcherAnimation;
        if (minimizeAnimation) {
          minimizeAnimation.onfinish = null;
          minimizeAnimation.oncancel = null;
        }
        if (this.dialog.open) {
          this.skipNextDialogClose = true;
          this.dialog.close();
        }
        minimizeAnimation?.cancel();
        launcherAnimation?.cancel();
        this.minimizeAnimation = null;
        this.launcherAnimation = null;
        this.dialog.classList.remove("is-minimizing");
        this.launcher.classList.remove("is-animation-target");
        this.launcher.removeAttribute("aria-hidden");
        this.launcher.removeAttribute("tabindex");
        this.minimizeTargetTrigger?.classList.remove("is-activity-minimize-target");
        this.minimizeTargetTrigger = null;
        this.showLauncher({ focus: settings.focus });
      };

      if (
        settings.immediate
        || reducedMotion
        || typeof this.dialog.animate !== "function"
        || (!dockFace && typeof this.launcher.animate !== "function")
      ) {
        finalize();
        return;
      }

      if (dockFace) {
        this.launcher.hidden = true;
        this.minimizeTargetTrigger?.classList.add("is-activity-minimize-target");
      } else {
        this.launcher.hidden = false;
        this.launcher.setAttribute("aria-expanded", "false");
        this.launcher.setAttribute("aria-hidden", "true");
        this.launcher.setAttribute("tabindex", "-1");
        this.launcher.classList.add("is-animation-target");
        this.positionLauncher();
      }

      const dialogRect = this.dialog.getBoundingClientRect();
      const launcherRect = animationTarget.getBoundingClientRect();
      const dialogCenterX = dialogRect.left + dialogRect.width / 2;
      const dialogCenterY = dialogRect.top + dialogRect.height / 2;
      const launcherCenterX = launcherRect.left + launcherRect.width / 2;
      const launcherCenterY = launcherRect.top + launcherRect.height / 2;
      const translateX = launcherCenterX - dialogCenterX;
      const translateY = launcherCenterY - dialogCenterY;
      const targetScale = Math.max(
        0.045,
        Math.min(
          0.16,
          launcherRect.width / Math.max(dialogRect.width, 1),
          launcherRect.height / Math.max(dialogRect.height, 1)
        )
      );
      const duration = 420;
      const easing = "cubic-bezier(0.22, 0.78, 0.2, 1)";
      const targetTransform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${targetScale})`;

      this.minimizeAnimation = this.dialog.animate([
        {
          opacity: 1,
          transform: "translate3d(0, 0, 0) scale(1)",
          transformOrigin: "center center",
        },
        {
          offset: 0.58,
          opacity: 0.94,
          transform: `translate3d(${translateX * 0.58}px, ${translateY * 0.58}px, 0) scale(${0.46 + targetScale * 0.54})`,
          transformOrigin: "center center",
        },
        {
          opacity: 0.06,
          transform: targetTransform,
          transformOrigin: "center center",
        },
      ], {
        duration,
        easing,
        fill: "forwards",
      });
      if (!dockFace) {
        this.launcherAnimation = this.launcher.animate([
          { opacity: 0, transform: "scale(0.72)", offset: 0 },
          { opacity: 0, transform: "scale(0.72)", offset: 0.55 },
          { opacity: 0.72, transform: "scale(0.9)", offset: 0.82 },
          { opacity: 1, transform: "scale(1)", offset: 1 },
        ], {
          duration,
          easing,
          fill: "forwards",
        });
      }
      this.minimizeAnimation.onfinish = finalize;
      this.minimizeAnimation.oncancel = () => {};
      this.minimizeFallbackTimer = window.setTimeout(finalize, duration + 120);
    }

    showLauncher(options) {
      if (!this.launcher || this.isEmbedded) return;
      const settings = Object.assign({ focus: false }, options || {});
      const dockTrigger = this.visibleDockTrigger();
      this.syncDockTriggerState(false);
      if (dockTrigger) {
        this.dataset.popupState = "token";
        this.launcher.classList.remove("is-animation-target");
        this.launcher.removeAttribute("aria-hidden");
        this.launcher.removeAttribute("tabindex");
        this.launcher.hidden = true;
        this.launcher.setAttribute("aria-expanded", "false");
        if (settings.focus) {
          window.requestAnimationFrame(() => dockTrigger.focus({ preventScroll: true }));
        }
        return;
      }

      this.dataset.popupState = "launcher";
      this.launcher.classList.remove("is-animation-target");
      this.launcher.removeAttribute("aria-hidden");
      this.launcher.removeAttribute("tabindex");
      this.launcher.hidden = false;
      this.launcher.setAttribute("aria-expanded", "false");
      this.positionLauncher();
      this.scheduleLauncherPosition();
      if (settings.focus) {
        window.requestAnimationFrame(() => this.launcher.focus({ preventScroll: true }));
      }
    }

    cancelMinimizeAnimations() {
      this.minimizeRunId = (this.minimizeRunId || 0) + 1;
      this.pendingOpenTargetRect = null;
      if (this.openAnimation) {
        this.openAnimation.onfinish = null;
        this.openAnimation.oncancel = null;
        this.openAnimation.cancel();
        this.openAnimation = null;
      }
      if (this.minimizeFallbackTimer) {
        window.clearTimeout(this.minimizeFallbackTimer);
        this.minimizeFallbackTimer = null;
      }
      if (this.minimizeAnimation) {
        this.minimizeAnimation.onfinish = null;
        this.minimizeAnimation.oncancel = null;
        this.minimizeAnimation.cancel();
        this.minimizeAnimation = null;
      }
      if (this.launcherAnimation) {
        this.launcherAnimation.cancel();
        this.launcherAnimation = null;
      }
      this.dialog?.classList.remove("is-minimizing");
      this.launcher?.classList.remove("is-animation-target");
      this.launcher?.removeAttribute("aria-hidden");
      this.launcher?.removeAttribute("tabindex");
      this.minimizeTargetTrigger?.classList.remove("is-activity-minimize-target");
      this.minimizeTargetTrigger = null;
    }

    scheduleLauncherPosition() {
      if (!this.launcher || this.launcher.hidden) return;
      if (this.launcherFrame) window.cancelAnimationFrame(this.launcherFrame);
      this.launcherFrame = window.requestAnimationFrame(() => {
        this.launcherFrame = null;
        this.positionLauncher();
      });
    }

    viewportRect() {
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft || 0;
      const top = viewport?.offsetTop || 0;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      return {
        left,
        top,
        width,
        height,
        right: left + width,
        bottom: top + height,
      };
    }

    collisionObstacles(viewport) {
      const candidates = Array.from(document.querySelectorAll([
        ".two-nu-home-dock [data-dock-link]",
        ".two-nu-home-dock .two-nu-home-dock__control",
        ".two-nu-home-dock .two-nu-home-dock__cart",
        ".two-nu-home-dock .two-nu-home-dock__whatsapp",
        ".two-nu-home-dock [data-dock-popover]:not([hidden])",
        ".two-nu-page-dock > a",
        ".two-nu-page-dock > .two-nu-page-dock__control",
        ".two-nu-page-dock > .two-nu-page-dock__icon",
        ".two-nu-page-dock [data-two-nu-dock-popover]:not([hidden])",
        "[data-two-nu-whatsapp]",
        'a[href*="wa.me"]',
        ".shopify-chat",
      ].join(",")));
      const obstacles = new Map();

      candidates.forEach((candidate) => {
        if (this.contains(candidate) || !isVisible(candidate)) return;
        const dockShell = candidate.closest(".two-nu-home-dock, .two-nu-page-dock");
        // The production dock shell itself spans the entire viewport and has
        // pointer-events:none. Only its real visible controls/links/popovers
        // are collision regions; using the shell rect would push the launcher
        // incorrectly across the screen.
        const obstacle = dockShell ? candidate : fixedOrStickyAncestor(candidate);
        if (!obstacle || this.contains(obstacle) || !isVisible(obstacle)) return;
        const rect = obstacle.getBoundingClientRect();
        if (
          rect.right < viewport.left
          || rect.left > viewport.right
          || rect.bottom < viewport.top
          || rect.top > viewport.bottom
        ) return;
        obstacles.set(obstacle, rect);
      });

      return Array.from(obstacles.values());
    }

    positionLauncher() {
      if (!this.launcher || this.isEmbedded) return;
      const viewport = this.viewportRect();
      const launcherRect = this.launcher.getBoundingClientRect();
      const size = Math.max(launcherRect.width, launcherRect.height, 52);
      const baseInset = window.innerWidth <= 749 ? 12 : 24;
      let rightOffset = window.innerWidth - viewport.right + baseInset;
      let bottomOffset = window.innerHeight - viewport.bottom + baseInset;

      const rectFor = () => {
        const right = window.innerWidth - rightOffset;
        const bottom = window.innerHeight - bottomOffset;
        return {
          left: right - size,
          right,
          top: bottom - size,
          bottom,
          width: size,
          height: size,
        };
      };

      for (const obstacle of this.collisionObstacles(viewport)) {
        let launcher = rectFor();
        if (!intersectsWithGap(launcher, obstacle, COLLISION_GAP)) continue;

        const bottomObstacle = obstacle.top >= viewport.top + viewport.height * 0.52
          && (
            obstacle.width >= viewport.width * 0.45
            || obstacle.bottom >= viewport.bottom - 96
          );
        const rightSideObstacle = !bottomObstacle && (
          obstacle.left >= viewport.left + viewport.width * 0.52
          || obstacle.right >= viewport.right - 72
        );

        if (bottomObstacle) {
          bottomOffset = Math.max(
            bottomOffset,
            window.innerHeight - (obstacle.top - COLLISION_GAP)
          );
        } else if (rightSideObstacle) {
          rightOffset = Math.max(
            rightOffset,
            window.innerWidth - (obstacle.left - COLLISION_GAP)
          );
        } else {
          const leftShift = launcher.right - obstacle.left + COLLISION_GAP;
          const upShift = launcher.bottom - obstacle.top + COLLISION_GAP;
          if (leftShift <= upShift) rightOffset += Math.max(0, leftShift);
          else bottomOffset += Math.max(0, upShift);
        }

        const maxRightOffset = window.innerWidth - (viewport.left + COLLISION_GAP + size);
        const maxBottomOffset = window.innerHeight - (viewport.top + COLLISION_GAP + size);
        rightOffset = Math.min(rightOffset, Math.max(baseInset, maxRightOffset));
        bottomOffset = Math.min(bottomOffset, Math.max(baseInset, maxBottomOffset));
        launcher = rectFor();
      }

      const rightValue = `${Math.max(0, rightOffset)}px`;
      const bottomValue = `${Math.max(0, bottomOffset)}px`;
      if (this.style.getPropertyValue("--two-nu-selector-launcher-right") !== rightValue) {
        this.style.setProperty("--two-nu-selector-launcher-right", rightValue);
      }
      if (this.style.getPropertyValue("--two-nu-selector-launcher-bottom") !== bottomValue) {
        this.style.setProperty("--two-nu-selector-launcher-bottom", bottomValue);
      }
    }

    emitAnalytics(configKey, extra) {
      const eventName = this.config.analytics?.[configKey];
      if (!eventName) return;
      const selected = this.selectedIds();
      const detail = Object.assign({
        event: eventName,
        locale: String(this.config.localeKey || ""),
        selectionCount: selected.length,
        routeKey: this.cta?.dataset.routeKey || "",
        openMode: this.openMode || "",
      }, extra || {});

      if (Array.isArray(window.dataLayer)) window.dataLayer.push(detail);
      this.dispatchEvent(new CustomEvent("two-nu:activity-selector-analytics", {
        bubbles: true,
        detail,
      }));
    }
  }

  customElements.define("two-nu-activity-selector", TwoNuActivitySelector);
})();
